import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { extractBody, wrapHTML } from "./htmlHelpers";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import "../userManagement/style.css";
import "./edit.css";


function slugFromTitle(title) {
  return String(title || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/^-+|-+$/g, "");
}

const EditFeaturedCategory = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get("id");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [excelFile, setExcelFile] = useState(null);

  const [form, setForm] = useState({
    tagName: "",
    pageUrl: "",
    landingDescription: "",
    status: "active",
    content: [],
    landingProductsCount: 6,
    categoryPageProductsCount: 20,
    landingLayoutPriority: 1,
  });

  /* ---------------- FETCH CATEGORY ---------------- */
  const fetchFeaturedCategory = async () => {
    try {
      const authData = getAuthLocalStorage();
      const token = authData.loginHeader;

      if (!token) {
        alert("Please login to access this page");
        navigate("/admin/login");
        return;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_APIUrl}admin/featuredCategory`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const category = res.data.data.find((i) => i._id === id);

      if (!category) {
        alert("Category not found");
        return navigate("/admin/featured-categories");
      }

      const rawContent = (category.content || []).map(item => ({
        ...item,
        _id: item._id || item.id || Math.random().toString(36).substr(2, 9),
        visible: item.visible !== false,
      }));

      const listBlocks = rawContent.filter((c) => c.type === "list");
      const nonListContent = rawContent.filter((c) => c.type !== "list");
      const mergedListData = listBlocks
        .map((b) => (b.data || "").trim())
        .filter(Boolean)
        .join(",");
      const mergedListHeading = listBlocks[0]?.heading || "";
      const singleListBlock =
        listBlocks.length > 0
          ? [
              {
                type: "list",
                data: mergedListData,
                heading: mergedListHeading,
                _id: listBlocks[0]._id || listBlocks[0].id || Math.random().toString(36).substr(2, 9),
                visible: true,
              },
            ]
          : [
              {
                type: "list",
                data: "",
                heading: "",
                _id: Math.random().toString(36).substr(2, 9),
                visible: true,
              },
            ];

      setForm({
        tagName: category.tagName,
        pageUrl: category.pageUrl || slugFromTitle(category.tagName),
        landingDescription: category.landingDescription || "",
        status: category.status,
        content: [...nonListContent, ...singleListBlock],
        landingProductsCount: Math.max(1, Number(category.landingProductsCount) || 6),
        categoryPageProductsCount: Math.max(1, Number(category.categoryPageProductsCount) || 20),
        landingLayoutPriority: [1, 2, 3].includes(Number(category.landingLayoutPriority)) ? Number(category.landingLayoutPriority) : 1,
      });

      setLoading(false);
    } catch (err) {
      console.error("Error fetching category:", err);
      if (err.response?.status === 401 || err.response?.status === 402) {
        alert("Your session has expired. Please login again.");
        navigate("/admin/login");
      } else {
        alert(err.response?.data?.message || "Failed to load category");
      }
    }
  };

  useEffect(() => {
    if (id) {
      fetchFeaturedCategory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ---------------- ADD / REMOVE CONTENT BLOCKS ---------------- */
  const addContentBlock = (type) => {
    const newBlock = {
      type,
      data: type === "html" ? "<p></p>" : "",
      heading: "",
      _id: Math.random().toString(36).substr(2, 9),
      visible: true,
    };
    setForm({ ...form, content: [...form.content, newBlock] });
  };

  const removeContentBlock = (index) => {
    const block = form.content[index];
    if (block && block.type === "list") return; // SKU list is permanent, cannot remove
    const updated = form.content.filter((_, i) => i !== index);
    setForm({ ...form, content: updated });
  };

  /* ---------------- UPDATE CONTENT ---------------- */
const updateContent = (index, value) => {
  const updated = [...form.content];

  updated[index] = {
    ...updated[index],
    data: value,          // ONLY update data
    _id: updated[index]._id || updated[index].id || Math.random().toString(36).substr(2, 9) // Preserve _id
  };

  setForm({ ...form, content: updated });
};


  /* ---------------- SAVE CATEGORY ---------------- */
 const handleSave = async () => {
  if (!form.tagName.trim()) {
    alert("Tag Name is required");
    return;
  }

  setSaving(true);
  try {
    // Prepare content array with proper structure
    const contentToSave = form.content.map(item => ({
      _id: item._id || item.id,
      type: item.type,
      data: item.data || "",
      heading: item.heading || "",
      visible: item.visible !== false,
    }));

    const authData = getAuthLocalStorage();
    const token = authData.loginHeader;

    if (!token) {
      alert("Please login again");
      navigate("/admin/login");
      return;
    }

    const productsCount = form.landingProductsCount === "" || form.landingProductsCount == null
      ? 6
      : Math.max(1, Number(form.landingProductsCount));

    const categoryPageCount = form.categoryPageProductsCount === "" || form.categoryPageProductsCount == null
      ? 20
      : Math.max(1, Number(form.categoryPageProductsCount));

    const pageUrl = (form.pageUrl || "").trim().replace(/^\//, "") || slugFromTitle(form.tagName);
    if (!pageUrl) {
      alert("Page URL is required (e.g. new-arrival).");
      setSaving(false);
      return;
    }

    const response = await axios.put(
      `${process.env.REACT_APP_APIUrl}admin/featuredCategory/${id}`,
      {
        tagName: form.tagName.trim(),
        pageUrl,
        landingDescription: (form.landingDescription || "").trim(),
        status: form.status,
        content: contentToSave,
        landingProductsCount: productsCount,
        categoryPageProductsCount: categoryPageCount,
        landingLayoutPriority: form.landingLayoutPriority,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (response.data && response.data.status && response.data.status.code === 200) {
      alert("Featured Category saved successfully!");
      navigate("/admin/featured-categories");
    } else {
      throw new Error(response.data?.message || "Save failed");
    }
  } catch (err) {
    console.error("SAVE ERROR:", err.response?.data || err);
    const errorMessage = err.response?.data?.message || err.response?.data?.error?.message || err.message || "Save failed";
    alert(`Failed to save: ${errorMessage}`);
  } finally {
    setSaving(false);
  }
};


  /* ---------------- EXCEL UPLOAD ---------------- */
  const uploadExcel = async () => {
    if (!excelFile) return alert("Please select a file");
    if (!form.tagName.trim()) return alert("Save the category name first, then upload.");

    const fd = new FormData();
    fd.append("file", excelFile);
    fd.append("tagName", form.tagName.trim());

    try {
      const authData = getAuthLocalStorage();
      const token = authData.loginHeader;

      if (!token) {
        alert("Please login again");
        return;
      }

      await axios.put(
        `${process.env.REACT_APP_APIUrl}admin/featuredCategory/product`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert("Products uploaded successfully. They are now linked to this category.");
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Excel upload failed");
    }
  };

  if (loading) return (
    <div className="fce-page">
      <div className="fce-loading">
        <div className="fce-loading-spinner" />
        <p>Loading category…</p>
      </div>
    </div>
  );

  return (
    <div className="fce-page">
      {/* Header */}
      <div className="fce-header">
        <button type="button" className="fce-back-btn" onClick={() => navigate("/admin/featured-categories")}>
          ← Back to List
        </button>
        <h1 className="fce-title">Edit Featured Category</h1>
      </div>

      {/* Main settings card */}
      <div className="fce-card">
        <div className="fce-grid">

          {/* Row 1: Title | Page URL */}
          <div className="fce-field">
            <label className="fce-label">Title <span className="fce-required">*</span></label>
            <input
              value={form.tagName}
              onChange={(e) => setForm((prev) => ({
                ...prev,
                tagName: e.target.value,
                pageUrl: prev.pageUrl === slugFromTitle(prev.tagName) ? slugFromTitle(e.target.value) : prev.pageUrl,
              }))}
              placeholder="e.g. Top Ranking, New Arrivals"
              className="fce-input"
            />
            <p className="fce-hint">Section title shown on the landing page.</p>
          </div>

          <div className="fce-field">
            <label className="fce-label">Page URL</label>
            <input
              value={form.pageUrl}
              onChange={(e) => setForm({ ...form, pageUrl: e.target.value.replace(/^\//, "") })}
              placeholder="e.g. top-ranking, new-arrivals"
              className="fce-input fce-input--mono"
            />
            <p className="fce-hint">URL slug for this category. Example: if set to &quot;my-category&quot;, the page will be /featured-category/my-category</p>
          </div>

          {/* Row 2: Description (full) */}
          <div className="fce-field fce-field--full">
            <label className="fce-label">Description</label>
            <input
              value={form.landingDescription}
              onChange={(e) => setForm({ ...form, landingDescription: e.target.value })}
              placeholder="e.g. Navigate trends with data-driven rankings"
              className="fce-input"
            />
            <p className="fce-hint">Shown below the title on the landing page. Leave empty to hide.</p>
          </div>

          {/* Row 3: Home products | Category products | Landing display */}
          <div className="fce-field">
            <label className="fce-label">Home page products</label>
            <input
              type="number"
              min={1}
              step={1}
              value={form.landingProductsCount}
              onChange={(e) => {
                const raw = e.target.value.trim();
                if (raw === "") { setForm({ ...form, landingProductsCount: "" }); return; }
                const n = parseInt(raw, 10);
                if (!Number.isNaN(n) && n >= 1) setForm({ ...form, landingProductsCount: n });
              }}
              onBlur={() => {
                const v = Number(form.landingProductsCount);
                if (!v || v < 1) setForm({ ...form, landingProductsCount: 6 });
              }}
              className="fce-input fce-input--num"
            />
            <p className="fce-hint">Products shown in the <strong>home page slider</strong></p>
          </div>

          <div className="fce-field">
            <label className="fce-label">Category page products</label>
            <input
              type="number"
              min={1}
              step={1}
              value={form.categoryPageProductsCount}
              onChange={(e) => {
                const raw = e.target.value.trim();
                if (raw === "") { setForm({ ...form, categoryPageProductsCount: "" }); return; }
                const n = parseInt(raw, 10);
                if (!Number.isNaN(n) && n >= 1) setForm({ ...form, categoryPageProductsCount: n });
              }}
              onBlur={() => {
                const v = Number(form.categoryPageProductsCount);
                if (!v || v < 1) setForm({ ...form, categoryPageProductsCount: 20 });
              }}
              className="fce-input fce-input--num"
            />
            <p className="fce-hint">Products shown on the <strong>category page</strong></p>
          </div>

          <div className="fce-field">
            <label className="fce-label">Landing display</label>
            <select
              value={form.landingLayoutPriority}
              onChange={(e) => setForm({ ...form, landingLayoutPriority: Number(e.target.value) })}
              className="fce-select"
            >
              <option value={1}>Default – 5 products per row, new row when more</option>
              <option value={3}>Single line row – visible cards with arrow to see more</option>
              <option value={2}>2 categories sided – paired with next category (left/right)</option>
            </select>
          </div>

        </div>
      </div>

      {/* Content Blocks */}
      <div className="fce-card">
        <div className="fce-card-head">
          <div>
            <h2 className="fce-card-title">Content Blocks</h2>
            <p className="fce-card-desc">Add HTML sections to display on the category page. The SKU list below is always included by default.</p>
          </div>
          <button type="button" className="fce-btn fce-btn--primary" onClick={() => addContentBlock("html")}>
            + Add HTML Section
          </button>
        </div>
      </div>

      {form.content.map((block, index) => (
        <div key={block._id || block.id || index} className="fce-card fce-block">
          {block.type === "html" && (
            <>
              <div className="fce-block-header">
                <h3 className="fce-block-title">Edit Section</h3>
                <div className="fce-block-controls">
                  <label className="fce-toggle-wrap">
                    <input
                      type="checkbox"
                      checked={block.visible !== false}
                      onChange={(e) => {
                        const updated = [...form.content];
                        updated[index] = { ...updated[index], visible: e.target.checked, _id: updated[index]._id || updated[index].id || Math.random().toString(36).substr(2, 9) };
                        setForm({ ...form, content: updated });
                      }}
                      className="fc-toggle-input"
                    />
                    <span className="fc-toggle-slider" />
                    <span className="fc-toggle-label">{block.visible !== false ? "Show" : "Hide"}</span>
                  </label>
                  <button type="button" className="fce-btn fce-btn--danger" onClick={() => removeContentBlock(index)}>
                    Remove
                  </button>
                </div>
              </div>
              <CKEditor
                editor={ClassicEditor}
                data={extractBody(block.data)}
                onChange={(event, editor) => {
                  const editedBody = editor.getData();
                  updateContent(index, wrapHTML(editedBody, block.data));
                }}
                config={{ toolbar: ["heading","|","bold","italic","link","|","bulletedList","numberedList","|","insertTable","imageUpload","|","undo","redo"] }}
              />
            </>
          )}

          {block.type === "list" && (
            <div className="fc-sku-list-block">
              <h4 className="fc-sku-list-title">Product SKU List</h4>
              <p className="fc-sku-list-desc">Enter product SKUs or offer IDs, comma or newline separated. These products will be shown on this category page.</p>
              <div className="fc-sku-list-fields">
                <label className="fc-sku-list-label">SKUs / Offer IDs</label>
                <textarea
                  className="fc-sku-list-textarea"
                  rows={5}
                  value={block.data}
                  onChange={(e) => updateContent(index, e.target.value)}
                  placeholder="e.g. 789067498695, 921478354716, 838190780492"
                />
                <label className="fc-sku-list-sublabel">Optional section heading</label>
                <input
                  className="fc-sku-list-heading"
                  value={block.heading || ""}
                  onChange={(e) => {
                    const updated = [...form.content];
                    updated[index] = { ...updated[index], heading: e.target.value, _id: updated[index]._id || updated[index].id || Math.random().toString(36).substr(2, 9) };
                    setForm({ ...form, content: updated });
                  }}
                  placeholder="e.g. Featured Products"
                />
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Bulk Upload */}
      <div className="fce-card">
        <h2 className="fce-card-title">Bulk Product Upload (Excel)</h2>
        <p className="fce-card-desc">
          Only a <strong>sku</strong> column is required. Products will be added to this category (<strong>{form.tagName || "—"}</strong>). Optional: add a <strong>tagName</strong> column to assign rows to other categories.
        </p>
        <div className="fce-upload-row">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
            className="fce-file-input"
          />
          <button
            type="button"
            onClick={uploadExcel}
            disabled={!excelFile || !form.tagName.trim()}
            className="fce-btn fce-btn--primary"
          >
            Upload Excel
          </button>
        </div>
        {!form.tagName.trim() && <p className="fce-hint fce-hint--warn">Save the category name above first, then upload.</p>}
      </div>

      {/* Actions */}
      <div className="fce-actions">
        <button type="button" onClick={() => navigate("/admin/featured-categories")} disabled={saving} className="fce-btn fce-btn--secondary">
          Cancel
        </button>
        <button type="button" onClick={handleSave} disabled={saving || !form.tagName.trim()} className="fce-btn fce-btn--save">
          {saving ? "Saving…" : "Save Featured Category"}
        </button>
      </div>
    </div>
  );
};

export default EditFeaturedCategory;
